import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';

// Animation utility classes and components for mobile-first design
export const animations = {
  // Entrance animations
  fadeIn: 'animate-fadeIn',
  slideInUp: 'animate-slideInUp',
  slideInDown: 'animate-slideInDown',
  slideInLeft: 'animate-slideInLeft',
  slideInRight: 'animate-slideInRight',
  scaleIn: 'animate-scaleIn',
  bounceIn: 'animate-bounceIn',

  // Exit animations
  fadeOut: 'animate-fadeOut',
  slideOutUp: 'animate-slideOutUp',
  slideOutDown: 'animate-slideOutDown',
  slideOutLeft: 'animate-slideOutLeft',
  slideOutRight: 'animate-slideOutRight',
  scaleOut: 'animate-scaleOut',

  // Attention animations
  pulse: 'animate-pulse',
  bounce: 'animate-bounce',
  shake: 'animate-shake',
  wiggle: 'animate-wiggle',

  // Loading animations
  spin: 'animate-spin',
  ping: 'animate-ping',

  // Interaction animations
  buttonPress: 'active:scale-95 transition-transform duration-150',
  cardHover: 'hover:scale-[1.02] transition-transform duration-300',
  iconHover: 'hover:scale-110 transition-transform duration-200',
};

// Animation Hook
export const useAnimation = () => {
  const { preferences } = useTheme();
  const reducedMotion = preferences.reducedMotion;

  const getAnimation = (animationClass: string) => {
    return reducedMotion ? '' : animationClass;
  };

  return {
    animations,
    getAnimation,
    reducedMotion,
  };
};

// Stagger Animation Container
interface StaggerContainerProps {
  children: React.ReactNode;
  delay?: number;
  className?: string;
  direction?: 'up' | 'down' | 'left' | 'right';
}

export const StaggerContainer: React.FC<StaggerContainerProps> = ({
  children,
  delay = 100,
  className = '',
  direction = 'up',
}) => {
  const { preferences } = useTheme();
  const reducedMotion = preferences.reducedMotion;

  const childrenArray = React.Children.toArray(children);

  const getDirectionClass = () => {
    switch (direction) {
      case 'down':
        return 'animate-slideInDown';
      case 'left':
        return 'animate-slideInLeft';
      case 'right':
        return 'animate-slideInRight';
      default:
        return 'animate-slideInUp';
    }
  };

  if (reducedMotion) {
    return <div className={className}>{children}</div>;
  }

  return (
    <div className={className}>
      {childrenArray.map((child, index) => (
        <div
          key={index}
          className={`${getDirectionClass()} opacity-0`}
          data-delay={index * delay}
        >
          {child}
        </div>
      ))}
    </div>
  );
};

// Fade Transition Component
interface FadeTransitionProps {
  show: boolean;
  children: React.ReactNode;
  duration?: number;
  className?: string;
}

export const FadeTransition: React.FC<FadeTransitionProps> = ({
  show,
  children,
  duration = 300,
  className = '',
}) => {
  const { preferences } = useTheme();
  const reducedMotion = preferences.reducedMotion;

  if (reducedMotion) {
    return show ? <div className={className}>{children}</div> : null;
  }

  return (
    <div
      className={`
        transition-all duration-${duration}
        ${
          show
            ? 'opacity-100 scale-100'
            : 'opacity-0 scale-95 pointer-events-none'
        }
        ${className}
      `}
    >
      {children}
    </div>
  );
};

// Slide Transition Component
interface SlideTransitionProps {
  show: boolean;
  children: React.ReactNode;
  direction?: 'up' | 'down' | 'left' | 'right';
  duration?: number;
  className?: string;
}

export const SlideTransition: React.FC<SlideTransitionProps> = ({
  show,
  children,
  direction = 'up',
  duration = 300,
  className = '',
}) => {
  const { preferences } = useTheme();
  const reducedMotion = preferences.reducedMotion;

  const getTransformClasses = () => {
    if (reducedMotion) return '';

    const transforms = {
      up: show ? 'translate-y-0' : 'translate-y-full',
      down: show ? 'translate-y-0' : '-translate-y-full',
      left: show ? 'translate-x-0' : 'translate-x-full',
      right: show ? 'translate-x-0' : '-translate-x-full',
    };

    return transforms[direction];
  };

  if (reducedMotion) {
    return show ? <div className={className}>{children}</div> : null;
  }

  return (
    <div
      className={`
        transition-all duration-${duration} ease-out
        ${show ? 'opacity-100' : 'opacity-0'}
        ${getTransformClasses()}
        ${!show ? 'pointer-events-none' : ''}
        ${className}
      `}
    >
      {children}
    </div>
  );
};

// Loading Spinner Component
interface LoadingSpinnerProps {
  size?: 'sm' | 'md' | 'lg';
  color?: 'primary' | 'secondary' | 'white';
  className?: string;
}

export const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  size = 'md',
  color = 'primary',
  className = '',
}) => {
  const { preferences } = useTheme();
  const reducedMotion = preferences.reducedMotion;

  const getSizeClasses = () => {
    switch (size) {
      case 'sm':
        return 'w-4 h-4 border-2';
      case 'lg':
        return 'w-8 h-8 border-3';
      default:
        return 'w-6 h-6 border-2';
    }
  };

  const getColorClasses = () => {
    switch (color) {
      case 'secondary':
        return 'border-gray-300 border-t-gray-600 dark:border-gray-600 dark:border-t-gray-300';
      case 'white':
        return 'border-white/30 border-t-white';
      default:
        return 'border-blue-200 border-t-blue-600 dark:border-blue-800 dark:border-t-blue-400';
    }
  };

  return (
    <div
      className={`
        ${getSizeClasses()}
        ${getColorClasses()}
        rounded-full
        ${reducedMotion ? '' : 'animate-spin'}
        ${className}
      `}
    />
  );
};

// Pulse Animation Component
interface PulseAnimationProps {
  children: React.ReactNode;
  color?: string;
  duration?: number;
  className?: string;
}

export const PulseAnimation: React.FC<PulseAnimationProps> = ({
  children,
  color = 'bg-blue-400',
  duration = 2000,
  className = '',
}) => {
  const { preferences } = useTheme();
  const reducedMotion = preferences.reducedMotion;

  if (reducedMotion) {
    return <div className={className}>{children}</div>;
  }

  return (
    <div className={`relative ${className}`}>
      {children}

      {/* Pulse rings */}
      <div className='absolute inset-0 flex items-center justify-center pointer-events-none'>
        <div
          className={`absolute ${color} rounded-full opacity-75 animate-ping`}
          data-duration={duration}
        />
        <div
          className={`absolute ${color} rounded-full opacity-50 animate-ping`}
          data-duration={duration}
          data-delay={duration * 0.2}
        />
      </div>
    </div>
  );
};

// Progress Animation Component
interface ProgressAnimationProps {
  progress: number;
  color?: string;
  height?: number;
  animated?: boolean;
  className?: string;
}

export const ProgressAnimation: React.FC<ProgressAnimationProps> = ({
  progress,
  color = 'bg-blue-600',
  height = 8,
  animated = true,
  className = '',
}) => {
  const { preferences } = useTheme();
  const reducedMotion = preferences.reducedMotion;

  const animationClass =
    animated && !reducedMotion ? 'transition-all duration-500 ease-out' : '';

  return (
    <div
      className={`w-full bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden ${className}`}
      data-height={height}
    >
      <div
        className={`${color} h-full rounded-full progress-bar-fill ${animationClass} ${
          animated && !reducedMotion
            ? 'progress-bar-animated bg-gradient-to-r from-blue-600 via-blue-500 to-blue-400'
            : ''
        } dynamic-width`}
        {...{
          style: {
            '--progress-width': `${Math.min(100, Math.max(0, progress))}%`,
          } as React.CSSProperties,
        }}
      />
    </div>
  );
};

// Floating Action Button with animation
interface FloatingActionButtonProps {
  onClick: () => void;
  icon: React.ReactNode;
  label?: string;
  position?: 'bottom-right' | 'bottom-left' | 'bottom-center';
  size?: 'sm' | 'md' | 'lg';
  color?: string;
  className?: string;
}

export const FloatingActionButton: React.FC<FloatingActionButtonProps> = ({
  onClick,
  icon,
  label,
  position = 'bottom-right',
  size = 'md',
  color = 'bg-blue-600 hover:bg-blue-700',
  className = '',
}) => {
  const { preferences } = useTheme();
  const reducedMotion = preferences.reducedMotion;

  const getPositionClasses = () => {
    switch (position) {
      case 'bottom-left':
        return 'fixed bottom-6 left-6';
      case 'bottom-center':
        return 'fixed bottom-6 left-1/2 transform -translate-x-1/2';
      default:
        return 'fixed bottom-6 right-6';
    }
  };

  const getSizeClasses = () => {
    switch (size) {
      case 'sm':
        return 'w-12 h-12';
      case 'lg':
        return 'w-16 h-16';
      default:
        return 'w-14 h-14';
    }
  };

  const animationClasses = reducedMotion
    ? ''
    : 'hover:scale-110 active:scale-95 transition-all duration-300 animate-bounceIn';

  return (
    <button
      onClick={onClick}
      className={`
        ${getPositionClasses()}
        ${getSizeClasses()}
        ${color}
        ${animationClasses}
        rounded-full
        shadow-lg hover:shadow-xl
        flex items-center justify-center
        text-white
        z-50
        focus:outline-none focus:ring-4 focus:ring-blue-300 dark:focus:ring-blue-800
        ${className}
      `}
      aria-label={label}
    >
      <div
        className={`${
          size === 'lg' ? 'text-lg' : size === 'sm' ? 'text-sm' : 'text-base'
        }`}
      >
        {icon}
      </div>
    </button>
  );
};

// Page Transition Wrapper
interface PageTransitionProps {
  children: React.ReactNode;
  className?: string;
}

export const PageTransition: React.FC<PageTransitionProps> = ({
  children,
  className = '',
}) => {
  const { preferences } = useTheme();
  const reducedMotion = preferences.reducedMotion;

  if (reducedMotion) {
    return <div className={className}>{children}</div>;
  }

  return <div className={`animate-fadeIn ${className}`}>{children}</div>;
};

export default {
  animations,
  useAnimation,
  StaggerContainer,
  FadeTransition,
  SlideTransition,
  LoadingSpinner,
  PulseAnimation,
  ProgressAnimation,
  FloatingActionButton,
  PageTransition,
};
